import { Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactLabelNameEnum, PopulationDensityBenchmark, R1FactType } from '@/api/rest/resources/types/fact';
import { GraphFact } from '@/components/Charts/types';
import { NCCard, NCCard2SectionLayout, NCCardHeader } from '@/components/NCCard/NCCard';
import { PopulationDensityExplainer } from '@/components/NCExplainers/PopulationDensityExplainer';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotCardWrapper } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotCardWrapper';
import { PopulationDensityGraphTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/populationDensity/GraphTile';
import { PopulationDensityStatsTile } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/populationDensity/StatsTile';
import { isNullish } from '@/utils/booleans';

export const PopulationDensity = () => {
  const { t } = useTranslation();

  return (
    <PlotCardWrapper name={t('global.analysis.populationDensity')}>
      <PopulationDensityPlotCard />
    </PlotCardWrapper>
  );
};

const PopulationDensityPlotCard = () => {
  const { t } = useTranslation();

  const factNames = {
    graph: R1FactType.population_density_timeseries_graph,
    latestAnalysis: R1FactType.population_density,
    benchmark: R1FactType.population_density_benchmark_graph,
  };

  const facts = usePlotFacts(Object.values(factNames));

  const graphData = getFactFromFactArray<GraphFact[]>(facts, factNames.graph)?.value;
  const latestAnalysisFact = getFactFromFactArray<number>(facts, factNames.latestAnalysis);

  const benchmarkFact = {
    urbanCenter: getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark_population_density]: PopulationDensityBenchmark.urban_center,
    }),
    urbanCluster: getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark_population_density]: PopulationDensityBenchmark.urban_cluster,
    }),
    rural: getFactFromFactArray<GraphFact[]>(facts, factNames.benchmark, {
      [PlotReportFactLabelNameEnum.benchmark_population_density]: PopulationDensityBenchmark.rural,
    }),
  };

  const hasData = !isNullish(graphData) || !isNullish(latestAnalysisFact);

  if (!hasData) {
    return null;
  }

  return (
    <NCCard data-testid='population-density-nca' explainer={<PopulationDensityExplainer />}>
      <NCCardHeader title={t('global.analysis.populationDensity')} className='lg:grid-cols-[1fr_1fr]' />
      <NCCard2SectionLayout>
        <Stack className='items-center pt-4 sm:items-stretch sm:pt-0'>
          <PopulationDensityGraphTile graphData={graphData} benchmarkFact={benchmarkFact} />
        </Stack>
        <PopulationDensityStatsTile latestAnalysisFact={latestAnalysisFact} benchmarkFact={benchmarkFact} />
      </NCCard2SectionLayout>
    </NCCard>
  );
};
